import DataTablePaging from 'components/DataTable/DataTablePaging';
import { SalesOrderHistoryState } from 'features/sales/models/SalesOrderHistoryEntry';
import salesApi, { SalesOrderHistoryParams } from 'features/sales/sales.api';
import useDynamicQueryParams from 'hooks/useDynamicQueryParams';
import React, { useMemo } from 'react';
import HistoryEntryDisplay from '../HistoryEntryDisplay/HistoryEntryDisplay';
import SalesOrderStatusBadge from '../SalesOrderStatusBadge/SalesOrderStatusBadge';
import './SalesOrderHistoryTab.scss';

// These are the attributes of a sales order that we want to display on the history view
const historyFieldLabels: Record<string, string> = {
    customer_notes: 'External notes',
    eta: 'ETA',
    notes: 'Internal notes',
    order_status_id: 'Status',
    total_freight_override: 'Total freight override',
    tracking_information: 'Tracking information',
};

export default function SalesOrderHistoryTab({
    manufacturerOrderId,
}: {
    manufacturerOrderId: number;
}) {
    const [queryParams, , , paging, setPaging] = useDynamicQueryParams<SalesOrderHistoryParams>(
        {
            manufacturerOrderId,
        },
        {
            pageSize: 20,
        },
    );
    const query = salesApi.useSalesOrderHistoryQuery(queryParams);

    /** Map SalesOrderHistoryEntry to HistoryEntryDisplay params */
    const historyEntries = useMemo(() => {
        return query.data?.data.map(entry => {
            const entries = Object.entries(entry.new).filter(([k, value]) => {
                const key = k as keyof SalesOrderHistoryState;
                const oldVal = entry.old[key];
                return oldVal !== value && !!historyFieldLabels[key];
            });

            const log = entry.type === 'order-dl' ? entry.logs : null;

            // dont bother computing changedFields if we're displaying log
            const changedFields = log
                ? []
                : entries.map(([key, value]) => ({
                      label: historyFieldLabels[key] ?? '',
                      displayValue:
                          key === 'order_status_id' ? (
                              <SalesOrderStatusBadge
                                  statusId={value as number}
                                  size="small"
                              />
                          ) : (
                              value
                          ),
                  }));

            return {
                id: entry.id,
                log,
                changedFields,
                updatedBy: entry.manufacturer_employees_full_name,
                timestamp: entry.created_at,
            };
        });
    }, [query.data]);

    return (
        <div className="SalesOrderHistoryTab">
            {historyEntries?.map(entry => (
                <HistoryEntryDisplay
                    key={entry.id}
                    changedFields={entry.changedFields}
                    log={entry.log}
                    updatedBy={entry.updatedBy}
                    timestamp={entry.timestamp}
                />
            ))}

            {(query.data?.data.length ?? 0) > 0 && (
                <DataTablePaging
                    data={paging}
                    totalCount={query.data?.total}
                    onChange={setPaging}
                />
            )}
        </div>
    );
}
